import { inject } from '@angular/core';

import { select } from '@ngrx/store';
import { takeUntil } from 'rxjs/operators';

import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
import { GdprService } from '@core/gdpr-logic';
import { SpamVerificationService } from '@core/spam-verification-logic';

import { BulkActionsController } from './bulk-actions.controller';
import { type BulkAction, BulkActionType, type BulkItemBase } from './bulk-actions.types';

export abstract class ATSBulkActionsController<
  BulkItem extends BulkItemBase = BulkItemBase,
> extends BulkActionsController<BulkItem> {
  private readonly gdprSvc = inject(GdprService);
  private readonly spamVerificationService = inject(SpamVerificationService);

  #abilities = {
    deleteCandidates: false,
    manageCandidates: false,
    sendEmails: false,
    shareCandidates: false,
    viewCandidates: false,
    manageJobs: false,
  };

  protected constructor() {
    super();

    this.store
      .pipe(select(AbilitiesSelectors.abilityMap), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(enabledMap => {
        this.#abilities = {
          deleteCandidates: enabledMap[Ability.DeleteCandidates],
          manageCandidates: enabledMap[Ability.ManageCandidates],
          manageJobs: enabledMap[Ability.ManageJobs],
          sendEmails: enabledMap[Ability.SendEmails],
          shareCandidates: enabledMap[Ability.ShareCandidates],
          viewCandidates: enabledMap[Ability.ViewCandidates],
        };
        this.recalculateActions$.next();
      });
  }

  public override isActionPossible(action: BulkAction): boolean {
    switch (action.id) {
      case BulkActionType.SendMail:
        return (
          this.#abilities.manageCandidates &&
          this.#abilities.sendEmails &&
          (!this.spamVerificationService || !this.spamVerificationService.verificationRequired())
        );
      case BulkActionType.Extend:
        return this.gdprSvc.isGdprEnabled(true);
      case BulkActionType.Delete:
        return this.#abilities.deleteCandidates;
      case BulkActionType.Follow:
      case BulkActionType.Unfollow:
      case BulkActionType.AddTask:
        return this.#abilities.viewCandidates;
      case BulkActionType.Share:
        return this.#abilities.shareCandidates;
      default:
        return this.#abilities.manageCandidates;
    }
  }
}
